import { SchemaRepository, SchemaStore } from "@src/lib/schemas";
import { JSONSchema9 } from "../types/jsonSchema";
import { WalterStore } from "../properties/WalterStore";
import { deref } from "../walter/typeOptions/handler";
import { ConditionGroupData } from "@src/taskpane/components/ConditionBuilder/ConditionCodeGen";

interface RenderCondition {
  ui?: ConditionGroupData;
  code: string;
}

/**
 * This saves the schema into a persistent store.
 */
export class PersistentSchemaStore extends SchemaStore {
  constructor(private persistentStore: SchemaRepository) {
    super({});
  }

  /**
   * Removes fields from the schema that don't have corresponding references in the system.
   * This prevents orphaned or invalid fields from persisting in the schema.
   *
   * NOTE: This mutates the schema passed into it.
   */
  static async discardUnknownFields(schema: JSONSchema9) {
    const unknownFields: string[] = [];

    // Iterate over schema.properties to check for unknown fields
    if (schema.properties) {
      for (const [key, value] of Object.entries(schema.properties)) {
        const ref = typeof value === "object" && value.$ref;

        if (ref && deref(ref) === undefined) {
          unknownFields.push(key);
        }
      }
    }

    if (unknownFields.length > 0) {
      console.warn("Discarding unknown field types:", unknownFields);
      unknownFields.forEach((key) => delete schema.properties?.[key]);
    }
  }

  async load() {
    this.schema = await this.persistentStore.getSchema();
    await PersistentSchemaStore.discardUnknownFields(this.schema);
    this.dispatchEvent("change");
  }

  get renderCondition(): RenderCondition | undefined {
    if ("x-render-condition" in this.schema) {
      return this.schema["x-render-condition"] as RenderCondition | undefined;
    }

    return undefined;
  }

  async saveRenderCondition(renderCondition: RenderCondition | undefined) {
    // This is an unusual use of a JSONSchema9 object, but it's a convenient way to store the render condition because the schema already stored in the Word document and passed along to DOCTR.
    (this.schema as { "x-render-condition"?: RenderCondition })["x-render-condition"] = renderCondition;

    await this.save(this.schema);
    this.dispatchEvent("change");
  }

  /**
   * Replaces workspace template object definitions while preserving user-created fields.
   * This ensures that when switching workspaces, old definitions are completely replaced
   * rather than merged, preventing stale references and caching issues.
   */
  async replaceWorkspaceDefinitions(newTemplateObjectsSchema: JSONSchema9) {
    const mergedSchema = {
      type: "object" as const,
      properties: this.schema.properties || {}, // Preserve user fields
      definitions: newTemplateObjectsSchema.definitions || {}, // Replace definitions entirely
    };

    await this.save(mergedSchema);
  }

  /**
   * This persists the fields in the document settings before delegating to the parent class.
   */
  async save(newSchema?: JSONSchema9) {
    await this.persistentStore.setSchema(newSchema ?? this.schema).catch(console.error);
    await super.save(newSchema);
  }
}

/**
 * This holds the fields for the document.
 */
export const documentSchemaStore = new PersistentSchemaStore(WalterStore);
